import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageHeader from "../layout/PageHeader";
import AgentAlbertNoteDetailModal from "./AgentAlbertNoteDetailModal";
import ShiftCheckInOutDetailModal from "./ShiftCheckInOutDetailModal";
import {
  fetchAgentAlbertAlerts,
  patchAgentAlbertAlertFollowUpStatus,
  AGENT_ALERTS_FETCH_LIMIT,
} from "../../utils/api";
import {
  countUnreadAgentAlbertAlerts,
  formatAgentAlbertAlertSeenBy,
  formatAgentAlbertAlertSeenByTitle,
  getStableAgentAlbertAlertId,
  markAgentAlbertAlertSeen,
} from "../../utils/agentAlbertAlertReadState";

const sortAlertsDesc = (rows) =>
  [...(Array.isArray(rows) ? rows : [])].sort((a, b) => {
    const at = new Date(a?.occurredAt || 0).getTime();
    const bt = new Date(b?.occurredAt || 0).getTime();
    return bt - at;
  });

const severityRank = (value) => {
  const s = String(value || "").toLowerCase();
  if (s === "critical") return 3;
  if (s === "high") return 2;
  if (s === "medium") return 1;
  return 0;
};

/** Matches AI Hiring Hire / Archive / Reject button colors */
const severityBadgeStyle = (severityRaw) => {
  const severity = String(severityRaw || "").toLowerCase();
  if (severity === "high" || severity === "critical") {
    return {
      background: "#ef4444",
      color: "#ffffff",
      border: "none",
    };
  }
  if (severity === "medium") {
    return {
      background: "#f59e0b",
      color: "#1f2937",
      border: "none",
    };
  }
  return {
    background: "#16a34a",
    color: "#ffffff",
    border: "none",
  };
};

const formatAlertDate = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  });
};

const formatAlertTime = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
};

const FOLLOW_UP_STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "following_up", label: "Following up" },
  { value: "callback_scheduled", label: "Callback scheduled" },
  { value: "resolved", label: "Resolved" },
  { value: "escalated", label: "Escalated" },
  { value: "deleted", label: "Delete" },
];
const DELETED_FOLLOW_UP_STATUS = "deleted";

const FOLLOW_UP_STATUS_SET = new Set(FOLLOW_UP_STATUS_OPTIONS.map((o) => o.value));

const normalizeFollowUpStatus = (raw) => {
  const v = String(raw || "").trim();
  return FOLLOW_UP_STATUS_SET.has(v) ? v : "open";
};

const formatSourceLabel = (source) => {
  const s = String(source || "").toLowerCase();
  if (s === "voice_call") return "Voice call";
  if (s === "visit_note") return "Visit note";
  if (s === "check_in") return "Check-in";
  if (s === "check_out") return "Check-out";
  return s ? s.replace(/_/g, " ") : "--";
};

const AlbertAiAlertsPage = (props) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [noteModalAlert, setNoteModalAlert] = useState(null);
  const [shiftModalAlert, setShiftModalAlert] = useState(null);
  const [statusSavingId, setStatusSavingId] = useState("");
  const handledAlertFromUrlRef = useRef("");

  const locationId = useMemo(
    () => String(props.activeLocation?._id || props.activeLocation?.id || "").trim(),
    [props.activeLocation],
  );
  const alertFromQuery = useMemo(
    () => String(searchParams.get("alert") || "").trim(),
    [searchParams],
  );

  const loadAlerts = useCallback(async () => {
    if (!locationId) {
      setAlerts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const payload = await fetchAgentAlbertAlerts(locationId, AGENT_ALERTS_FETCH_LIMIT);
      setAlerts(
        sortAlertsDesc(Array.isArray(payload?.alerts) ? payload.alerts : []).filter(
          // Defense-in-depth: the server already strips deleted alerts before responding
          // (services/agentAlbertAlerts.js, fixed July 2026), so this filter is now
          // redundant but kept as a safety net in case of API version skew or
          // stale cached responses.
          (row) => normalizeFollowUpStatus(row?.followUpStatus) !== DELETED_FOLLOW_UP_STATUS,
        ),
      );
    } catch (err) {
      setAlerts([]);
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load shift reports.",
        );
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  // handledAlertFromUrlRef guards against re-firing on unstable `alerts`/`setSearchParams`
  // deps below — do not remove without replacing the guard.
  // (Previously caused a replaceState() infinite loop — see 2026-07-02 fix.)
  useEffect(() => {
    loadAlerts();
    if (!locationId) return undefined;
    const interval = setInterval(loadAlerts, 60000);
    return () => clearInterval(interval);
  }, [loadAlerts, locationId]);

  useEffect(() => {
    if (!alertFromQuery) {
      handledAlertFromUrlRef.current = "";
      return;
    }
    if (handledAlertFromUrlRef.current === alertFromQuery) return;
    if (alerts.length === 0) return;
    const match = alerts.find((a) => getStableAgentAlbertAlertId(a) === alertFromQuery);
    if (!match) return;
    handledAlertFromUrlRef.current = alertFromQuery;
    if (locationId) {
      const updated = markAgentAlbertAlertSeen(locationId, match);
      setAlerts((prev) =>
        prev.map((a) =>
          getStableAgentAlbertAlertId(a) === getStableAgentAlbertAlertId(match)
            ? updated
            : a,
        ),
      );
      setSelectedAlert(updated);
    } else {
      setSelectedAlert(match);
    }
    setSearchParams(
      (prev) => {
        if (!prev.get("alert")) return prev;
        const next = new URLSearchParams(prev);
        next.delete("alert");
        return next;
      },
      { replace: true },
    );
  }, [alertFromQuery, alerts, locationId, setSearchParams]);

  const stats = useMemo(() => {
    const total = alerts.length;
    const unread = countUnreadAgentAlbertAlerts(alerts, locationId);
    const highSeverity = alerts.filter((a) => {
      const s = String(a?.severity || "").toLowerCase();
      return s === "high" || s === "critical";
    }).length;
    const uniqueClients = new Set(
      alerts.map((a) => String(a?.clientId || "").trim()).filter(Boolean),
    ).size;
    return { total, unread, highSeverity, uniqueClients };
  }, [alerts, locationId]);

  const typeOptions = useMemo(
    () =>
      Array.from(
        new Set(alerts.map((a) => String(a?.title || "").trim()).filter(Boolean)),
      ).sort(),
    [alerts],
  );

  const filteredAlerts = useMemo(() => {
    const q = String(searchQuery || "").trim().toLowerCase();
    return alerts.filter((alert) => {
      const severity = String(alert?.severity || "").toLowerCase();
      const source = String(alert?.source || "").trim();
      const title = String(alert?.title || "").trim();
      if (severityFilter !== "all" && severity !== severityFilter) return false;
      if (sourceFilter !== "all" && source !== sourceFilter) return false;
      if (typeFilter !== "all" && title !== typeFilter) return false;
      if (!q) return true;
      const haystack = [
        alert?.clientName,
        alert?.title,
        alert?.reason,
        alert?.excerpt,
        alert?.caregiverName,
        alert?.source,
        alert?.severity,
      ]
        .map((v) => String(v || "").toLowerCase())
        .join(" ");
      return haystack.includes(q);
    });
  }, [alerts, searchQuery, severityFilter, sourceFilter, typeFilter]);

  const applySeenToAlertInList = (alert) => {
    if (!locationId || !alert) return;
    const updated = markAgentAlbertAlertSeen(locationId, alert);
    const alertId = getStableAgentAlbertAlertId(alert);
    setAlerts((prev) =>
      prev.map((a) => (getStableAgentAlbertAlertId(a) === alertId ? updated : a)),
    );
    if (
      selectedAlert &&
      getStableAgentAlbertAlertId(selectedAlert) === alertId
    ) {
      setSelectedAlert(updated);
    }
  };

  const openClientFile = (alert) => {
    if (!alert?.clientId) return;
    applySeenToAlertInList(alert);
    setSelectedAlert(null);
    setNoteModalAlert(null);
    const source = String(alert?.source || "").toLowerCase();
    const isShiftSource = source === "check_in" || source === "check_out";
    const params = new URLSearchParams({
      tab: isShiftSource ? "shiftQuestions" : "visitNote",
    });
    if (alert?.appointmentId) {
      params.set("appointmentId", String(alert.appointmentId));
    }
    navigate(
      `/client-management/client/${encodeURIComponent(alert.clientId)}/aip-analysis?${params.toString()}`,
      { state: isShiftSource ? { openShiftQuestions: true } : { openVisitNote: true } },
    );
  };

  const openAgentAlbertNote = (alert) => {
    if (!alert?.clientId) return;
    applySeenToAlertInList(alert);
    if (!alert?.voiceCallId && !alert?.visitNoteId) return;
    setNoteModalAlert(alert);
  };

  const openShiftQuestions = (alert) => {
    if (!alert?.clientId || !alert?.appointmentId) return;
    if (locationId) markAgentAlbertAlertSeen(locationId, alert);
    setShiftModalAlert(alert);
  };

  const handleFollowUpStatusChange = async (row, followUpStatus) => {
    const alertId = getStableAgentAlbertAlertId(row);
    if (!alertId || !locationId) return;
    if (followUpStatus === DELETED_FOLLOW_UP_STATUS) {
      const ok = window.confirm("Delete this alert from the list?");
      if (!ok) return;
    }
    setStatusSavingId(alertId);
    setError("");
    try {
      const updated = await patchAgentAlbertAlertFollowUpStatus(
        locationId,
        alertId,
        followUpStatus,
      );
      const nextStatus = normalizeFollowUpStatus(updated?.followUpStatus);
      if (nextStatus === DELETED_FOLLOW_UP_STATUS) {
        setAlerts((prev) =>
          prev.filter((a) => getStableAgentAlbertAlertId(a) !== alertId),
        );
        setSelectedAlert((prev) =>
          prev && getStableAgentAlbertAlertId(prev) === alertId ? null : prev,
        );
        return;
      }
      setAlerts((prev) =>
        sortAlertsDesc(
          prev.map((a) =>
            getStableAgentAlbertAlertId(a) === alertId
              ? { ...a, followUpStatus: nextStatus }
              : a,
          ),
        ),
      );
      setSelectedAlert((prev) =>
        prev && getStableAgentAlbertAlertId(prev) === alertId
          ? { ...prev, followUpStatus: nextStatus }
          : prev,
      );
    } catch {
      setError("Failed to save status. Please try again.");
    } finally {
      setStatusSavingId("");
    }
  };

  const openViewClient = (alert) => {
    if (!alert?.clientId) return;
    const source = String(alert?.source || "").toLowerCase();
    if (source === "check_in" || source === "check_out") {
      openShiftQuestions(alert);
      return;
    }
    openAgentAlbertNote(alert);
  };

  return (
    <div className="page-shell ai-hiring-page">
      <PageHeader
        title="Shift Report"
        subtitle="AI summary flags from shift report notes and shift calls."
        icon={
          <span role="img" aria-label="Shift Report">
            🧠
          </span>
        }
        {...props}
      />
      <div className="ai-hiring-content">
        {!locationId ? (
          <div className="ai-empty">Select a location to view shift reports.</div>
        ) : loading ? (
          <div className="ai-empty">Loading shift reports...</div>
        ) : (
          <>
            {error ? <div className="ai-alert ai-alert-error">{error}</div> : null}
            <div className="ai-settings-grid">
              <div className="ai-card ai-settings-card">
                <p className="ai-card-note">Total Alerts</p>
                <h3 className="ai-card-title">{stats.total}</h3>
              </div>
              <div className="ai-card ai-settings-card">
                <p className="ai-card-note">Unread</p>
                <h3 className="ai-card-title">{stats.unread}</h3>
              </div>
              <div className="ai-card ai-settings-card">
                <p className="ai-card-note">High / Critical</p>
                <h3 className="ai-card-title">{stats.highSeverity}</h3>
              </div>
              <div className="ai-card ai-settings-card">
                <p className="ai-card-note">Clients</p>
                <h3 className="ai-card-title">{stats.uniqueClients}</h3>
              </div>
            </div>

            <div className="ai-card">
              <div className="ai-card-header">
                <div>
                  <p className="ai-card-note">Shift Report — AI Summary</p>
                  <h3 className="ai-card-title">Recent Alert Events</h3>
                </div>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr 1fr 1fr",
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <input
                  className="ai-input"
                  placeholder="Search client, alert, reason, excerpt..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <select
                  className="ai-input"
                  value={severityFilter}
                  onChange={(e) => setSeverityFilter(e.target.value)}
                >
                  <option value="all">All Severity</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                </select>
                <select
                  className="ai-input"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <option value="all">All Alert Types</option>
                  {typeOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <select
                  className="ai-input"
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                >
                  <option value="all">All Sources</option>
                  <option value="voice_call">Voice call</option>
                  <option value="visit_note">Visit note</option>
                  <option value="check_in">Check-in</option>
                  <option value="check_out">Check-out</option>
                </select>
              </div>
              <div className="ai-table-wrapper">
                <table className="ai-table ai-table-conversation-events" style={{ minWidth: 1420 }}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Client</th>
                      <th>Alert</th>
                      <th>Severity</th>
                      <th>Detected</th>
                      <th>Source</th>
                      <th>Caregiver</th>
                      <th>Status</th>
                      <th>Seen by</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAlerts.length === 0 ? (
                      <tr>
                        <td colSpan="10">No matching shift reports.</td>
                      </tr>
                    ) : (
                      filteredAlerts.map((alert) => {
                        const alertId = getStableAgentAlbertAlertId(alert);
                        return (
                          <tr
                            key={alertId}
                            style={{
                              cursor: "pointer",
                              borderBottom: "1px solid #e2e8f0",
                              background: "#ffffff",
                            }}
                            onClick={() => {
                              if (locationId) {
                                const updated = markAgentAlbertAlertSeen(
                                  locationId,
                                  alert,
                                );
                                setAlerts((prev) =>
                                  prev.map((a) =>
                                    getStableAgentAlbertAlertId(a) === alertId
                                      ? updated
                                      : a,
                                  ),
                                );
                                setSelectedAlert(updated);
                              } else {
                                setSelectedAlert(alert);
                              }
                            }}
                          >
                            <td>
                              <div>{formatAlertDate(alert?.occurredAt)}</div>
                              <div style={{ color: "#64748b", fontSize: 12 }}>
                                {formatAlertTime(alert?.occurredAt)}
                              </div>
                            </td>
                            <td>{alert?.clientName || "Unknown client"}</td>
                            <td>{alert?.title || "AI alert"}</td>
                            <td>
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  borderRadius: 999,
                                  padding: "4px 10px",
                                  fontSize: 12,
                                  fontWeight: 700,
                                  textTransform: "capitalize",
                                  ...severityBadgeStyle(alert?.severity),
                                }}
                              >
                                {alert?.severity || "--"}
                              </span>
                            </td>
                            <td title={alert?.reason || ""}>
                              {alert?.reason || "--"}
                            </td>
                            <td>{formatSourceLabel(alert?.source)}</td>
                            <td>{alert?.caregiverName || "—"}</td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <select
                                className="ai-conversation-status-select"
                                value={normalizeFollowUpStatus(alert?.followUpStatus)}
                                onChange={(e) =>
                                  handleFollowUpStatusChange(alert, e.target.value)
                                }
                                disabled={
                                  statusSavingId === getStableAgentAlbertAlertId(alert)
                                }
                                aria-label="Follow-up status"
                              >
                                {FOLLOW_UP_STATUS_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td
                              title={formatAgentAlbertAlertSeenByTitle(
                                alert?.seenBy,
                              )}
                              style={{ maxWidth: 200 }}
                            >
                              {formatAgentAlbertAlertSeenBy(alert?.seenBy)}
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                className="ai-button ai-button-secondary"
                                onClick={() => openViewClient(alert)}
                                disabled={
                                  !alert?.clientId ||
                                  ((alert?.source === "check_in" ||
                                    alert?.source === "check_out") &&
                                    !alert?.appointmentId)
                                }
                              >
                                View client
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {selectedAlert ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.45)",
            zIndex: 1200,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: 16,
          }}
          onClick={() => setSelectedAlert(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(900px, 96vw)",
              maxHeight: "88vh",
              overflowY: "auto",
              background: "#fff",
              borderRadius: 18,
              border: "1px solid #dbeafe",
              boxShadow: "0 20px 40px rgba(15, 23, 42, 0.18)",
              padding: 18,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                marginBottom: 12,
              }}
            >
              <div>
                <h3 style={{ margin: 0, fontSize: 20, color: "#0f172a" }}>
                  {selectedAlert?.clientName || "Client"}
                  {selectedAlert?.title ? ` — ${selectedAlert.title}` : ""}
                </h3>
                <div style={{ color: "#64748b", marginTop: 4 }}>
                  {formatAlertDate(selectedAlert?.occurredAt)}
                  {" · "}
                  {formatSourceLabel(selectedAlert?.source)}
                  {selectedAlert?.caregiverName
                    ? ` · ${selectedAlert.caregiverName}`
                    : ""}
                </div>
                <div style={{ color: "#64748b", marginTop: 4, fontSize: 13 }}>
                  <strong>Seen by:</strong>{" "}
                  {formatAgentAlbertAlertSeenBy(selectedAlert?.seenBy)}
                </div>
              </div>
              <button
                type="button"
                className="ai-button ai-button-secondary"
                onClick={() => setSelectedAlert(null)}
              >
                Close
              </button>
            </div>

            <div style={{ marginBottom: 14, maxWidth: 340 }}>
              <p className="ai-card-note" style={{ margin: "0 0 6px" }}>
                Status
              </p>
              <select
                className="ai-conversation-status-select ai-conversation-status-select--modal"
                value={normalizeFollowUpStatus(selectedAlert?.followUpStatus)}
                onChange={(e) =>
                  handleFollowUpStatusChange(selectedAlert, e.target.value)
                }
                disabled={
                  statusSavingId === getStableAgentAlbertAlertId(selectedAlert)
                }
                aria-label="Follow-up status"
              >
                {FOLLOW_UP_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 12,
                marginBottom: 12,
              }}
            >
              <div
                className="ai-card ai-settings-card"
                style={{ background: "#f8fbff", borderRadius: 12 }}
              >
                <p className="ai-card-note">Severity</p>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    borderRadius: 999,
                    padding: "4px 10px",
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: "capitalize",
                    ...severityBadgeStyle(selectedAlert?.severity),
                  }}
                >
                  {selectedAlert?.severity || "--"}
                </span>
              </div>
              <div
                className="ai-card ai-settings-card"
                style={{ background: "#f8fbff", borderRadius: 12 }}
              >
                <p className="ai-card-note">Detected phrase</p>
                <h3 className="ai-card-title" style={{ fontSize: 16 }}>
                  {selectedAlert?.reason?.replace(/^Detected:\s*/i, "") || "—"}
                </h3>
              </div>
            </div>

            <div className="ai-card">
              <div className="ai-card-header">
                <div>
                  <p className="ai-card-note">AI Summary excerpt</p>
                  <h3 className="ai-card-title">Details</h3>
                </div>
              </div>
              <div
                style={{
                  color: "#0f172a",
                  lineHeight: 1.6,
                  whiteSpace: "pre-line",
                }}
              >
                {selectedAlert?.excerpt || "No excerpt available."}
              </div>
            </div>

            <div
              style={{
                marginTop: 14,
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
              }}
            >
              <button
                type="button"
                className="ai-button ai-button-secondary"
                onClick={() => openClientFile(selectedAlert)}
                disabled={!selectedAlert?.clientId}
              >
                Open client file
              </button>
              <button
                type="button"
                className="ai-button ai-button-primary"
                onClick={() => openViewClient(selectedAlert)}
                disabled={
                  !selectedAlert?.clientId ||
                  ((String(selectedAlert?.source || "").toLowerCase() ===
                    "check_in" ||
                    String(selectedAlert?.source || "").toLowerCase() ===
                      "check_out") &&
                    !selectedAlert?.appointmentId)
                }
              >
                {String(selectedAlert?.source || "").toLowerCase() === "check_in" ||
                String(selectedAlert?.source || "").toLowerCase() === "check_out"
                  ? "View shift questions"
                  : "View Shift Report Note"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <AgentAlbertNoteDetailModal
        alert={noteModalAlert}
        isOpen={Boolean(noteModalAlert)}
        onClose={() => setNoteModalAlert(null)}
      />

      <ShiftCheckInOutDetailModal
        alert={shiftModalAlert}
        isOpen={Boolean(shiftModalAlert)}
        onClose={() => setShiftModalAlert(null)}
      />
    </div>
  );
};

export default AlbertAiAlertsPage;
